import { useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Users,
  MousePointerClick,
  Eye,
  ArrowDownUp,
  Clock,
  FileStack,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { Period, PeriodOption, BreakdownProperty } from './analytics/types';
import {
  useOverview,
  useTimeseries,
  useBreakdown,
  useRealtime,
} from './analytics/hooks';

/* ── constants ─────────────────────────────────────────────── */

const PERIODS: PeriodOption[] = [
  { value: 'day', label: 'Heute' },
  { value: '7d', label: '7 Tage' },
  { value: '30d', label: '30 Tage' },
  { value: 'month', label: 'Monat' },
  { value: '6mo', label: '6 Monate' },
  { value: '12mo', label: '12 Monate' },
];

const BREAKDOWN_TABS: { property: BreakdownProperty; label: string }[] = [
  { property: 'source', label: 'Quellen' },
  { property: 'page', label: 'Seiten' },
  { property: 'device', label: 'Geräte' },
  { property: 'browser', label: 'Browser' },
  { property: 'country', label: 'Länder' },
];

/* ── small shared components ───────────────────────────────── */

function Spinner() {
  return (
    <div className="flex h-32 items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-xl border border-surface-300 bg-surface-100 text-neutral-400">
      <AlertTriangle className="h-6 w-6 text-yellow-500" />
      <p className="max-w-md text-center text-xs">{message}</p>
    </div>
  );
}

/* ── format helpers ────────────────────────────────────────── */

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function fmtNumber(n: number): string {
  return n.toLocaleString('de-DE');
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (dateStr.length <= 10) {
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/* ── Period Selector ───────────────────────────────────────── */

function PeriodSelector({
  value,
  onChange,
}: {
  value: Period;
  onChange: (p: Period) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            value === p.value
              ? 'bg-brand-600 text-white'
              : 'bg-surface-200 text-neutral-400 hover:bg-surface-300 hover:text-neutral-200'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

/* ── Realtime Indicator ────────────────────────────────────── */

function RealtimeIndicator({ siteId }: { siteId: string }) {
  const visitors = useRealtime(siteId);
  if (visitors === null) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-200 px-3 py-1.5">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
      </span>
      <span className="text-xs font-medium text-neutral-200">
        {visitors} Besucher jetzt
      </span>
    </div>
  );
}

/* ── Metric Cards ──────────────────────────────────────────── */

interface MetricCardDef {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  format: (v: number) => string;
}

const METRIC_CARDS: MetricCardDef[] = [
  { key: 'visitors', label: 'Besucher', icon: Users, format: fmtNumber },
  { key: 'visits', label: 'Besuche', icon: MousePointerClick, format: fmtNumber },
  { key: 'pageviews', label: 'Seitenaufrufe', icon: Eye, format: fmtNumber },
  { key: 'bounce_rate', label: 'Absprungrate', icon: ArrowDownUp, format: (v) => `${v}%` },
  { key: 'visit_duration', label: 'Besuchsdauer', icon: Clock, format: fmtDuration },
  { key: 'views_per_visit', label: 'Seiten/Besuch', icon: FileStack, format: (v) => v.toFixed(1) },
];

function MetricCards({
  siteId,
  period,
}: {
  siteId: string;
  period: Period;
}) {
  const { data, loading, error } = useOverview(siteId, period);

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorBox message={error ?? 'Übersicht konnte nicht geladen werden.'} />;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {METRIC_CARDS.map((card) => {
        const Icon = card.icon;
        const value = data[card.key as keyof typeof data] as number;
        return (
          <div
            key={card.key}
            className="rounded-xl border border-surface-300 bg-surface-100 p-4"
          >
            <div className="flex items-center gap-2 text-neutral-500">
              <Icon className="h-3.5 w-3.5" />
              <span className="text-xs">{card.label}</span>
            </div>
            <p className="mt-2 text-xl font-semibold text-neutral-100">
              {card.format(value)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* ── Chart Tooltip ─────────────────────────────────────────── */

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ dataKey?: string; color?: string; value?: number }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-surface-300 bg-surface-50 px-3 py-2 shadow-lg">
      <p className="mb-1 text-xs text-neutral-400">{fmtDate(label ?? '')}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="text-xs font-medium" style={{ color: entry.color }}>
          {entry.dataKey === 'visitors' ? 'Besucher' : 'Seitenaufrufe'}: {fmtNumber(entry.value ?? 0)}
        </p>
      ))}
    </div>
  );
}

/* ── Time Series Chart ─────────────────────────────────────── */

function TimeseriesChart({
  siteId,
  period,
}: {
  siteId: string;
  period: Period;
}) {
  const { data, loading, error } = useTimeseries(siteId, period);

  if (loading) return <Spinner />;
  if (error) return <ErrorBox message={error} />;
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-surface-300 bg-surface-100 text-sm text-neutral-500">
        Keine Daten für diesen Zeitraum.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-surface-300 bg-surface-100 p-4">
      <h3 className="mb-4 flex items-center gap-2 text-sm font-medium text-neutral-200">
        <BarChart3 className="h-4 w-4 text-brand-400" />
        Besucher &amp; Seitenaufrufe
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
          <defs>
            <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradPageviews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={fmtDate}
            tick={{ fontSize: 11, fill: '#525252' }}
            axisLine={false}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#525252' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<ChartTooltip />} />
          <Area
            type="monotone"
            dataKey="pageviews"
            stroke="#60a5fa"
            strokeWidth={1.5}
            fill="url(#gradPageviews)"
            dot={false}
            activeDot={{ r: 3, fill: '#60a5fa' }}
          />
          <Area
            type="monotone"
            dataKey="visitors"
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#gradVisitors)"
            dot={false}
            activeDot={{ r: 4, fill: '#3b82f6' }}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center justify-center gap-6 text-xs text-neutral-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-brand-500" />
          Besucher
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-brand-400" />
          Seitenaufrufe
        </span>
      </div>
    </div>
  );
}

/* ── Breakdown Tables ──────────────────────────────────────── */

function BreakdownTables({
  siteId,
  period,
}: {
  siteId: string;
  period: Period;
}) {
  const [activeTab, setActiveTab] = useState<BreakdownProperty>('source');
  const { data, loading, error } = useBreakdown(siteId, period, activeTab);

  return (
    <div className="rounded-xl border border-surface-300 bg-surface-100">
      {/* tabs */}
      <div className="flex gap-0 overflow-x-auto border-b border-surface-300">
        {BREAKDOWN_TABS.map((t) => (
          <button
            key={t.property}
            type="button"
            onClick={() => setActiveTab(t.property)}
            className={`whitespace-nowrap px-4 py-3 text-xs font-medium transition-colors ${
              activeTab === t.property
                ? 'border-b-2 border-brand-500 text-brand-400'
                : 'text-neutral-500 hover:text-neutral-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* content */}
      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="p-4">
          <ErrorBox message={error} />
        </div>
      ) : data.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-neutral-500">
          Keine Daten für diesen Zeitraum.
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-surface-300 text-xs uppercase text-neutral-500">
              <th className="px-5 py-3 font-medium">
                {BREAKDOWN_TABS.find((t) => t.property === activeTab)?.label}
              </th>
              <th className="px-5 py-3 text-right font-medium">Besucher</th>
              <th className="px-5 py-3 text-right font-medium">Absprungrate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-300">
            {data.map((row) => {
              const maxVisitors = data[0]?.visitors ?? 1;
              const pct = Math.round((row.visitors / maxVisitors) * 100);
              return (
                <tr key={row.dimension} className="group relative hover:bg-surface-200/50 transition-colors">
                  <td className="relative px-5 py-3 text-neutral-200">
                    <div
                      className="absolute inset-y-0 left-0 bg-brand-500/10 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                    <span className="relative">{row.dimension}</span>
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-neutral-300">
                    {fmtNumber(row.visitors)}
                  </td>
                  <td className="px-5 py-3 text-right text-neutral-500">
                    {row.bounce_rate}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Main Export ────────────────────────────────────────────── */

export function AnalyticsTab({ siteId }: { siteId: string }) {
  const [period, setPeriod] = useState<Period>('30d');

  return (
    <div className="space-y-4">
      {/* Toolbar: period selector + realtime */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodSelector value={period} onChange={setPeriod} />
        <RealtimeIndicator siteId={siteId} />
      </div>

      {/* Metric Cards */}
      <MetricCards siteId={siteId} period={period} />

      {/* Time series chart */}
      <TimeseriesChart siteId={siteId} period={period} />

      {/* Breakdown tables */}
      <BreakdownTables siteId={siteId} period={period} />
    </div>
  );
}
